"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { cn } from "@/components/ui/cn";
import { useDict } from "@/components/i18n/DictProvider";
import type { Category } from "@/lib/lecturehub/types";

/** What a pill needs of a category. `children` is optional on purpose: lists
 *  DERIVED from rows (the posts index) don't carry the tree and simply render
 *  a flat rail. */
export type PillCategory = Pick<Category, "id" | "name" | "stub"> & {
  children?: Pick<Category, "id" | "name" | "stub">[] | null;
};

/**
 * The category rail — one quiet single row at every breakpoint, hierarchy on
 * demand (category-pills field study, 2026-09-01; NN/g filters guidance +
 * Baymard's horizontal-toolbar findings; the YouTube/Airbnb chip-rail and the
 * Google Play parent→children mechanics):
 *
 * - CHIPS ARE QUIET. Idle = a muted fill and nothing else — the old
 *   border-on-every-pill anatomy is what made 23 categories read as a
 *   component wall. Active = solid primary. No counts on chips (NN/g: counts
 *   where they inform a decision — the live total rides on band headers).
 * - ONE ROW, ANNOUNCED OVERFLOW. This file once rejected single-row scrolling
 *   ("2184px of chips behind an invisible edge") and wrapped on desktop
 *   instead. The rejection was right about a NAKED scroller; the fix the
 *   industry ships is the affordance, not the wrap: edge fades (a CSS mask,
 *   so it works over any ground) plus a scroll arrow ≥lg. The scrollbar is
 *   hidden — the fades and arrow are the signal.
 * - PARENTS FIRST, CHILDREN ON SELECTION. The rail shows root categories;
 *   selecting one reveals its children as a lighter second row (first entry =
 *   the parent's own filter, labelled t.listing.all). Backend child names are
 *   compound ("Engineering / Frontend"); the kid row strips the parent prefix
 *   — hierarchy is expressed by position, not a slash. A child whose parent
 *   was filtered out of the passed list is promoted to the rail rather than
 *   orphaned invisible; a child absent from the passed list (no matching
 *   modules) is dropped — the zero-results contract is the caller's filter.
 *
 * Filter SEMANTICS are unchanged: every chip is the same ?category=<stub>
 * link it always was; only the arrangement moved.
 */
export function CategoryPills({
  categories,
  current,
  basePath,
  preserveParams,
  pages,
}: {
  categories: PillCategory[];
  current: string | undefined;
  basePath: string;
  preserveParams?: Record<string, string | undefined>;
  /** Chips as PAGES instead of `basePath?category=` links: the news rail
   *  links each category to its own page (D31) — `${base}/<stub>` — and the
   *  "all" chip to `all`. Plain paths, not a function: this is a client
   *  component and its props cross the server boundary. */
  pages?: { all: string; base: string };
}) {
  const t = useDict();
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(preserveParams ?? {})) {
    if (v && k !== "category" && k !== "page") params.set(k, v);
  }
  const baseQuery = params.toString();

  function href(stub?: string): string {
    if (pages) return stub ? `${pages.base}/${encodeURIComponent(stub)}` : pages.all;
    const q = new URLSearchParams(baseQuery);
    if (stub) q.set("category", stub);
    const qs = q.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  // Tree over the flat, pre-filtered list: an id referenced by a PRESENT
  // row's children is a child; everything else (true roots + orphans whose
  // parent was filtered away) belongs on the rail.
  const presentIds = new Set(categories.map((c) => c.id));
  const childIds = new Set(
    categories
      .flatMap((c) => (c.children ?? []).map((k) => k.id))
      .filter((id) => presentIds.has(id)),
  );
  const roots = categories.filter((c) => !childIds.has(c.id));
  const activeRoot = current
    ? roots.find((r) => r.stub === current || (r.children ?? []).some((k) => k.stub === current))
    : undefined;
  const kids = (activeRoot?.children ?? []).filter((k) => presentIds.has(k.id));
  const kidLabel = (root: PillCategory, kid: { name: string }) => {
    const prefix = `${root.name} / `;
    return kid.name.startsWith(prefix) ? kid.name.slice(prefix.length) : kid.name;
  };

  // Overflow affordance state — fades mask the rail's own content (works on
  // any section ground), the arrow appears only while there is more to see.
  const railRef = useRef<HTMLUListElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const updateOverflow = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateOverflow();
    const el = railRef.current;
    if (!el) return;
    // Deep-linked active chip starts visible, not behind the fade.
    const active = el.querySelector<HTMLElement>('[aria-current="true"]');
    if (active) {
      el.scrollLeft = Math.max(0, active.offsetLeft - el.clientWidth / 2);
      updateOverflow();
    }
    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateOverflow, categories.length]);

  const fade = 44;
  const mask =
    canLeft && canRight
      ? `linear-gradient(to right, transparent, black ${fade}px, black calc(100% - ${fade}px), transparent)`
      : canRight
        ? `linear-gradient(to right, black calc(100% - ${fade}px), transparent)`
        : canLeft
          ? `linear-gradient(to right, transparent, black ${fade}px)`
          : undefined;

  const chip =
    // 32 px drawn; on a touch screen the pseudo-element takes the row to 44 px
    // without changing how the strip looks (the chips sit alone in their row).
    "relative inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full px-3.5 text-sm font-medium transition-colors pointer-coarse:before:absolute pointer-coarse:before:-inset-y-1.5 pointer-coarse:before:inset-x-0 pointer-coarse:before:content-['']";
  const chipIdle = "bg-muted text-body-soft hover:bg-light-gray/70 hover:text-foreground";
  const chipActive = "bg-primary text-primary-foreground font-semibold";
  const rootActive = (r: PillCategory) => activeRoot?.id === r.id;

  const kidChip =
    "relative inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full border px-3 text-xs font-medium transition-colors pointer-coarse:before:absolute pointer-coarse:before:-inset-y-2 pointer-coarse:before:inset-x-0 pointer-coarse:before:content-['']";
  const kidIdle =
    "border-light-gray bg-card text-mid-gray hover:border-primary/40 hover:text-primary";
  const kidActive = "border-primary bg-primary/10 text-primary font-semibold";

  return (
    <nav aria-label={t.listing.allCategories}>
      <div className="relative">
        <ul
          ref={railRef}
          onScroll={updateOverflow}
          className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ maskImage: mask, WebkitMaskImage: mask }}
        >
          <li className="shrink-0">
            <Link
              href={href()}
              className={cn(chip, !current ? chipActive : chipIdle)}
              aria-current={!current ? "true" : undefined}
            >
              {t.listing.allCategories}
            </Link>
          </li>
          {roots.map((c) => (
            <li key={c.id} className="shrink-0">
              <Link
                href={href(c.stub)}
                aria-current={rootActive(c) ? "true" : undefined}
                className={cn(chip, rootActive(c) ? chipActive : chipIdle)}
              >
                {c.name}
              </Link>
            </li>
          ))}
        </ul>
        {canRight ? (
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => {
              const el = railRef.current;
              el?.scrollBy({ left: el.clientWidth * 0.7, behavior: "smooth" });
            }}
            className="border-light-gray text-body-soft absolute top-1/2 right-0 hidden size-8 -translate-y-1/2 place-items-center rounded-full border bg-card transition-colors hover:text-primary lg:grid"
            style={{ boxShadow: "var(--shadow-soft)" }}
          >
            <ChevronRight className="size-4" />
          </button>
        ) : null}
      </div>

      {activeRoot && kids.length > 0 ? (
        <ul aria-label={activeRoot.name} className="mt-2.5 flex flex-wrap gap-1.5">
          <li>
            <Link
              href={href(activeRoot.stub)}
              aria-current={current === activeRoot.stub ? "true" : undefined}
              className={cn(kidChip, current === activeRoot.stub ? kidActive : kidIdle)}
            >
              {t.listing.all}
            </Link>
          </li>
          {kids.map((k) => (
            <li key={k.id}>
              <Link
                href={href(k.stub)}
                aria-current={current === k.stub ? "true" : undefined}
                className={cn(kidChip, current === k.stub ? kidActive : kidIdle)}
              >
                {kidLabel(activeRoot, k)}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </nav>
  );
}
